import { AlertCircle, CalendarClock, MessageSquare } from 'lucide-react';

import { Skeleton } from '@/components/ui/Skeleton';
import { formatCurrency } from '@/lib/format';
import { cn, getNameInitials } from '@/lib/utils';
import type { FeeAccountRow } from '@/types/fees';

/** How many families the panel names before it stops and counts the rest. */
const SHOWN = 5;

export interface FeeChaseListProps {
  /** The register's first page, largest balance first — the family to ring first. */
  accounts: FeeAccountRow[] | null;
  /** Today at the institute, `YYYY-MM-DD`, so every row counts from one day. */
  today: string;
  isLoading: boolean;
  /** How many accounts still owe anything, across the whole session. */
  owingCount: number;
  canRemind: boolean;
  onRemind: (account: FeeAccountRow) => void;
  /** Opens the account itself, where the full ledger and every receipt are. */
  onOpenAccount: (studentUuid: string) => void;
  /** Takes the register to every account, largest balance first. */
  onSeeAll: () => void;
}

/**
 * How a due date reads against today.
 */
function describeDue(
  dueDate: string,
  today: string,
): { text: string; tone: 'over' | 'soon' | 'later' } {
  const days = Math.round(
    (Date.parse(`${dueDate}T00:00:00.000Z`) - Date.parse(`${today}T00:00:00.000Z`)) / 86_400_000,
  );

  if (days < 0) {
    const late = Math.abs(days);
    return { text: `Overdue by ${late} ${late === 1 ? 'day' : 'days'}`, tone: 'over' };
  }
  if (days === 0) return { text: 'Due today', tone: 'over' };
  if (days === 1) return { text: 'Due tomorrow', tone: 'soon' };

  return { text: `Due in ${days} days`, tone: days <= 7 ? 'soon' : 'later' };
}

export function FeeChaseList({
  accounts,
  today,
  isLoading,
  owingCount,
  canRemind,
  onRemind,
  onOpenAccount,
  onSeeAll,
}: FeeChaseListProps) {
  const owing = (accounts ?? []).filter((account) => Number(account.balance) > 0).slice(0, SHOWN);
  const remaining = Math.max(0, owingCount - owing.length);

  return (
    <section className="rounded-2xl sm:rounded-3xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-[#111c2e] p-5 shadow-xs">
      <div className="flex items-center justify-between gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-amber-50 dark:bg-amber-950/80 text-amber-600 dark:text-amber-400 font-bold"
            aria-hidden
          >
            <AlertCircle className="h-4 w-4" />
          </span>
          <div className="min-w-0">
            <h2 className="truncate text-base font-extrabold text-slate-900 dark:text-white tracking-tight">
              Who to chase
            </h2>
            <p className="mt-0.5 truncate text-xs font-medium text-slate-500 dark:text-slate-400">
              Largest outstanding first
            </p>
          </div>
        </div>

        {owingCount > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="shrink-0 cursor-pointer text-xs font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 transition-colors hover:underline"
          >
            View all
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="mt-4 flex flex-col gap-3">
          {Array.from({ length: 3 }, (_, index) => (
            <Skeleton key={index} className="h-10 w-full rounded-xl" />
          ))}
        </div>
      ) : owing.length === 0 ? (
        <p className="mt-4 rounded-2xl border border-dashed border-slate-200 dark:border-slate-800 px-4 py-8 text-center text-sm font-medium text-slate-500 dark:text-slate-400">
          Nobody owes anything for this session. Nothing to chase.
        </p>
      ) : (
        <ul className="mt-2 flex flex-col divide-y divide-slate-100 dark:divide-slate-800">
          {owing.map((account) => {
            const due = account.dueDate ? describeDue(account.dueDate, today) : null;

            return (
              <li key={account.studentUuid} className="flex items-center gap-3 py-2.5">
                <button
                  type="button"
                  onClick={() => onOpenAccount(account.studentUuid)}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 rounded-xl text-left focus:outline-none"
                >
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-blue-50 dark:bg-blue-950/80 text-2xs font-bold text-blue-600 dark:text-blue-400"
                    aria-hidden
                  >
                    {getNameInitials(account.fullName)}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-bold text-slate-800 dark:text-slate-100">
                      {account.fullName}
                    </span>
                    <span className="mt-0.5 block truncate text-xs font-medium text-slate-500 dark:text-slate-400">
                      {account.className !== '—'
                        ? `${account.className} · ${account.sectionName}`
                        : account.admissionNumber}
                    </span>
                  </span>
                </button>

                <div className="shrink-0 text-right">
                  <p className="font-mono text-sm font-extrabold tabular-nums text-rose-600 dark:text-rose-400">
                    {formatCurrency(account.balance)}
                  </p>
                  {/* The account's own date, or the class's next unpaid
                      installment — named, so the office knows which term it is. */}
                  {due && (
                    <p
                      className={cn(
                        'mt-0.5 flex items-center justify-end gap-1 text-[11px] font-semibold',
                        due.tone === 'over'
                          ? 'text-rose-600 dark:text-rose-400'
                          : due.tone === 'soon'
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-slate-500 dark:text-slate-400',
                      )}
                    >
                      <CalendarClock className="h-3 w-3 shrink-0" aria-hidden />
                      {account.nextInstallment && account.nextInstallment.dueDate === account.dueDate
                        ? `${account.nextInstallment.name} · ${due.text}`
                        : due.text}
                    </p>
                  )}
                </div>

                {canRemind && (
                  <button
                    type="button"
                    aria-label={`Send ${account.fullName}'s family a fee reminder`}
                    onClick={() => onRemind(account)}
                    className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-xl border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-blue-600 hover:border-blue-300 dark:hover:text-blue-400 transition-colors shadow-2xs"
                  >
                    <MessageSquare className="h-3.5 w-3.5" aria-hidden />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {remaining > 0 && (
        <p className="mt-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400">
          and {remaining} more {remaining === 1 ? 'student owes' : 'students owe'} money
        </p>
      )}
    </section>
  );
}
